<script>
import useSharedData from "@/use/SharedData";
import {computed, onMounted, ref} from "vue";
import useSharedMethod from "@/use/SharedMethod";
import useTools from "@/use/Tools";
import axios from "axios";
import useValidation from "@/use/Validation";
import {useClipboard} from "@/use/Clipboard";
import {useRecruitmentSourcingStore} from "@/stores/Recruitment/RecruitmentSourcing";

export default {
    name: "ShareForm",
    setup() {
        const {mxRules} = useValidation();
        const {dialog, loading} = useSharedData();
        const {$route, $refs} = useTools();
        const {getBack, formatedDate} = useSharedMethod();
        const {copyToClipboard} = useClipboard();

        const recruitmentSourcingStore = useRecruitmentSourcingStore();

        const sharing = ref({
            sourcingId: $route.params.id,
            includeOccupation: false,
            includeGeneratedEmail: true,
            announcement: ""
        });

        const availablePlatforms = [
            {
                id: "FACEBOOK",
                name: "Facebook"
            },
            {
                id: "LINKEDIN",
                name: "Linkedin"
            }
        ];

        const sourcing = ref({cvTechs: []});

        function findSourcingById(id) {
            axios.get(`/recruitmentSourcings/${id}`).then(response => {
                sourcing.value = response.data;
            });
        }

        function findAll() {
            loading.value = true;
            axios
                .get("/recruitmentSharings", {
                    params: {
                        sourcingId: $route.params.id
                    }
                })
                .then(response => {
                    if (response.data.length > 0) {
                        sharing.value = response.data[0];
                    }
                })
                .finally(() => {
                    loading.value = false;
                });
        }

        const generatedEmail = computed(() => {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            let r = sharing.value.emailAddress;
            if (emailRegex.test(r)) {
                const charToInsertBefore = "@";
                const substringToInsert = `+${sourcing.value.requestCode}`;

                const index = r.indexOf(charToInsertBefore);
                r = r.slice(0, index) + substringToInsert + r.slice(index);
                return r;
            }
            return null;
        });

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                sharing.value.generatedReceiptEmail = generatedEmail.value;
                axios
                    .post("/recruitmentSharings", sharing.value)
                    .then(response => {
                        recruitmentSourcingStore.updateRecruitmentSourcing(response.data);
                        getBack();
                    })
                    .finally(() => {
                        loading.value = false;
                    });
            }
        }

        const selectedRadioOption = computed({
            get() {
                if (sharing.value.includeOccupation) return "occupation";
                if (sharing.value.includeGeneratedEmail) return "generatedEmail";
                return null;
            },
            set(value) {
                sharing.value.includeOccupation = value === "occupation";
                sharing.value.includeGeneratedEmail = value === "generatedEmail";
            }
        });

        onMounted(() => {
            findSourcingById($route.params.id);
            findAll();
        });

        return {
            mxRules,
            sourcing,
            dialog,
            loading,
            selectedRadioOption,
            sharing,
            generatedEmail,
            availablePlatforms,
            save,
            getBack,
            copyToClipboard,
            formatedDate
        };
    }
};
</script>

<template>
    <v-dialog v-model="dialog" persistent max-width="1500" scrollable>
        <v-card>
            <w-toolbar color="teal" :title="$t('recruitment_request.sharing')"/>
            <v-card-text>
                <v-container fluid>
                    <w-fieldset :title="$t('recruitment_request.request_info')">
                        <v-simple-table dense>
                            <template v-slot:default>
                                <thead>
                                <tr>
                                    <th class="text-left">{{ $t("common.code") }}</th>
                                    <th class="text-left">
                                        {{ $t("recruitment_request.occupation") }}
                                    </th>
                                    <th class="text-left">
                                        {{ $t("recruitment_request.recruitment_date") }}
                                    </th>
                                    <th class="text-left">
                                        {{ $t("recruitment_request.requesting_agency") }}
                                    </th>
                                    <th class="text-left">
                                        {{ $t("recruitment_request.post_number") }}
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr>
                                    <td class="text-left">
                                        <w-chip :text="sourcing.requestCode"/>
                                    </td>
                                    <td class="text-left">
                                        <w-inline-text
                                            bold
                                            :text="sourcing.requestOccupationName"
                                        />
                                    </td>
                                    <td class="text-left">
                                        <w-chip
                                            color="teal"
                                            :text="formatedDate(sourcing.requestExpectedDate)"
                                        />
                                    </td>
                                    <td class="text-left">
                                        <w-inline-text
                                            :text="sourcing.requestRequestingAgencyName"
                                        />
                                    </td>
                                    <td class="text-left">
                                        <w-chip outlined :text="sourcing.requestPostNumber"/>
                                    </td>
                                </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                    </w-fieldset>
                    <br/>
                    <v-form ref="form" lazy-validation>
                        <v-row dense>
                            <v-col cols="12" md="5">
                                <w-fieldset :title="$t('recruitment_request.receive')">
                                    <v-row dense>
                                        <v-col cols="12">
                                            <w-label :label="$t('common.email')" required/>
                                            <w-server-autocomplete
                                                v-model="sharing.emailAddress"
                                                item-value="address"
                                                api="emails"
                                                :readonly="sourcing.closed"
                                                :rules="[mxRules.required]"
                                            />
                                        </v-col>
                                        <v-col cols="12">
                                            <w-label
                                                :label="$t('recruitment_request.publish_on')"
                                                required
                                            />
                                            <w-date-picker
                                                v-model="sharing.publishOn"
                                                :readonly="sourcing.closed"
                                                :rules="[mxRules.required]"
                                            />
                                        </v-col>
                                    </v-row>
                                </w-fieldset>
                                <br/>
                                <w-fieldset
                                    v-if="sourcing.requestScope === 'EXTERNAL'"
                                    :title="$t('recruitment_request.platforms')"
                                >
                                    <w-enum-select
                                        v-model="sharing.platforms"
                                        :items="availablePlatforms"
                                        :readonly="sourcing.closed"
                                        multiple
                                    />
                                </w-fieldset>
                                <br/>
                                <w-fieldset :title="$t('common.options')">
                                    <v-row>
                                        <v-col cols="12">
                                            <v-radio-group
                                                v-model="selectedRadioOption"
                                                :readonly="sourcing.closed"
                                                :rules="[mxRules.required]"
                                            >
                                                <v-radio value="generatedEmail">
                                                    <template v-slot:label>
                                                        <span>
                                                          {{ $t('recruitment_request.share_option_one') }}
                                                          <w-btn-icon
                                                              icon="mdi-content-copy"
                                                              :tooltip="generatedEmail"
                                                              @press="copyToClipboard(generatedEmail)"
                                                          />
                                                        </span>
                                                    </template>
                                                </v-radio>
                                                <v-radio
                                                    value="occupation"
                                                    :label="$t('recruitment_request.share_option_two')"
                                                />
                                            </v-radio-group>
                                        </v-col>
                                    </v-row>
                                </w-fieldset>
                            </v-col>
                            <v-col cols="12" md="7">
                                <w-fieldset :title="$t('recruitment_request.announcement')">
                                    <v-row>
                                        <v-col cols="12">
                                            <w-simple-text-area
                                                v-model="sharing.announcement"
                                                :readonly="sourcing.closed"
                                                :rules="sourcing.requestScope === 'INTERNAL' ? [mxRules.required] : []"
                                            />
                                        </v-col>
                                    </v-row>
                                </w-fieldset>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-container>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    show
                    :disabled="sourcing.closed"
                    :loading="loading"
                    @press="save"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>